import React, { useState, useEffect, useRef, useCallback } from 'react';
import { convertEpochToDate } from '../../utils';
import { useSdpdsReadOnlyOperation } from '../../hooks/useSdpdsReadOnlyOperation';
import {
  EuiDatePicker,
  EuiInMemoryTable,
  EuiButtonEmpty,
  EuiButton,
  EuiOverlayMask,
  EuiModal,
  EuiModalHeader,
  EuiModalHeaderTitle,
  EuiModalBody,
  EuiDatePickerRange,
  EuiFlexGroup,
  EuiFlexItem,
} from '@elastic/eui';

import moment from 'moment';
import ContentPanel from '../../../Components/ContentPanel/ContentPanel';
import { pagination } from './V2/constants';

const HOURS_TO_MILLIS_MULTIPLIER = 60 * 60 * 1000;

export const ClusterAuditHistory = ({ stackName, clusterId }) => {
  const [startDate, setStartDate] = useState(moment().add(-1, 'days'));
  const [endDate, setEndDate] = useState(moment());
  const [items, setItems] = useState([]);
  const [isResponseUpdated, setIsResponseUpdated] = useState(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [statusCode, setStatusCode] = useState(null);
  const [nextToken, setNextToken] = useState(null);
  const [startTime, setStartTime] = useState(Math.floor((Date.now() - HOURS_TO_MILLIS_MULTIPLIER * 750) / 1000));
  const [endTime, setEndTime] = useState(Math.floor(Date.now() / 1000));

  const exceptionRef = useRef('');
  const itemsRef = useRef([]);
  const rowIdRef = useRef(1);

  const { loading, data, error } = useSdpdsReadOnlyOperation({
    operation_name: "ClusterAuditHistory",
    payload: {
      cluster: {
        stackName,
        clusterId,
      },
      startTime: startTime,
      endTime: endTime,
      maxResults: 50,
      nextToken: nextToken,
    }
  });

  const updateResult = useCallback((response) => {
    let i;
    let j;
    let length;
    let data;
    let componentScope;
    let deploymentDetails;
    let componentName;
    let majorVersion;
    let minorVersion;
    let componentAction;
    length = response.clusterUpdateList.length;

    for (i = 0; i < length; i++) {
      deploymentDetails = '';
      for (
        j = 0;
        j < response.clusterUpdateList[i].componentVersionUpdateList.length;
        j++
      ) {
        componentName =
          response.clusterUpdateList[i].componentVersionUpdateList[j]
            .componentVersion.componentName;
        majorVersion =
          response.clusterUpdateList[i].componentVersionUpdateList[j]
            .componentVersion.majorVersion;
        minorVersion =
          response.clusterUpdateList[i].componentVersionUpdateList[j]
            .componentVersion.minorVersion;
        componentScope =
          response.clusterUpdateList[i].componentVersionUpdateList[j]
            .componentVersion.componentScopeDefinition.componentScope;
        componentAction =
          response.clusterUpdateList[i].componentVersionUpdateList[j]
            .componentAction;
        deploymentDetails =
          deploymentDetails +
          ' ' +
          componentAction +
          '/' +
          componentScope +
          '/' +
          componentName +
          '-' +
          majorVersion +
          '-' +
          minorVersion;
      }
      data = {
        id: rowIdRef.current,
        deployment: rowIdRef.current,
        deploymentdetails: deploymentDetails,
        updatestatus: response.clusterUpdateList[i].updateStatus,
        creationTime: response.clusterUpdateList[i].creationTime,
      };

      rowIdRef.current++;
      itemsRef.current.push(data);
    }
    setItems([...itemsRef.current]);
  }, []);

  const updateException = useCallback((response) => {
    exceptionRef.current = JSON.stringify(response.body);
  }, []);

  const updateResponse = useCallback((response) => {
    setStatusCode(response.statusCode);
    if (response.statusCode === 200) {
      updateResult(response.body);
    } else {
      updateException(response);
    }
  }, [updateResult, updateException]);

  useEffect(() => {
    if (error) {
      const response = {
        statusCode: 500,
        body: error
      };
      updateResponse(response);
      setIsResponseUpdated(true);
    } else if (data?.sdpdsReadOnlyOperation) {
      const response = {
        statusCode: 200,
        body: data.sdpdsReadOnlyOperation
      };
      updateResponse(response);
      if (data.sdpdsReadOnlyOperation.nextToken) {
        setNextToken(data.sdpdsReadOnlyOperation.nextToken);
      } else {
        setIsResponseUpdated(true);
      }
    }
  }, [data, error, updateResponse]);

  useEffect(() => {
    itemsRef.current = [];
    rowIdRef.current = 1;
    setItems([]);
    setNextToken(null);
    setIsResponseUpdated(false);
  }, [startTime, endTime]);

  const setDuration = (duration) => {
    const now = Date.now();
    setEndTime(Math.floor(now / 1000));
    setStartTime(Math.floor((now - HOURS_TO_MILLIS_MULTIPLIER * (duration || 750)) / 1000));
  };

  const setDateRange = () => {
    const startMs = new Date(startDate.toDate()).getTime();
    const endMs = new Date(endDate.toDate()).getTime();
    setStartTime(Math.floor(startMs / 1000));
    setEndTime(Math.floor(endMs / 1000));
  };

  const closeModal = () => {
    setIsModalVisible(false);
  };

  const showModal = () => {
    setIsModalVisible(true);
  };

  const startDateHandler = (date) => {
    setStartDate(date);
  };

  const endDateHandler = (date) => {
    setEndDate(date);
  };

  const getDatePicker = () => {
    let modal = '';
    if (isModalVisible) {
      modal = (
        <EuiOverlayMask>
          <EuiModal
            style={{ minHeight: '450px', minWidth: '650px' }}
            onClose={closeModal}
            id="datepicker"
          >
            <EuiModalHeader>
              <EuiModalHeaderTitle>Set Date</EuiModalHeaderTitle>
            </EuiModalHeader>
            <EuiModalBody>
              <EuiFlexGroup>
                <EuiFlexItem>
                  <EuiDatePickerRange
                    style={{ float: 'left' }}
                    id="datepickerrange"
                    startDateControl={
                      <EuiDatePicker
                        selected={startDate}
                        onChange={startDateHandler}
                        startDate={startDate}
                        endDate={endDate}
                        isInvalid={startDate > endDate}
                        aria-label="Start date"
                        showTimeSelect
                      />
                    }
                    endDateControl={
                      <EuiDatePicker
                        selected={endDate}
                        onChange={endDateHandler}
                        startDate={startDate}
                        endDate={endDate}
                        isInvalid={startDate > endDate}
                        aria-label="End date"
                        showTimeSelect
                      />
                    }
                  />
                </EuiFlexItem>
                <EuiFlexItem grow={false}>
                  <EuiButton
                    id="datebutton"
                    textProps={{ id: 'rangesearch' }}
                    onClick={setDateRange}
                  >
                    Update
                  </EuiButton>
                </EuiFlexItem>
              </EuiFlexGroup>
            </EuiModalBody>
          </EuiModal>
        </EuiOverlayMask>
      );
    }
    return modal;
  };

  const renderToolsRight = () => {
    return [
      ...[
        { label: '1h', value: 1 },
        { label: '3h', value: 3 },
        { label: '12h', value: 12 },
        { label: '1d', value: 24 },
        { label: '3d', value: 72 },
        { label: '1w', value: 168 },
      ].map((opt) => (
        <EuiButtonEmpty key={opt.value} onClick={() => setDuration(opt.value)}>
          {opt.label}
        </EuiButtonEmpty>
      )),
      <EuiButtonEmpty
        key="custom"
        id="custom"
        iconType="arrowDown"
        iconSide="right"
        onClick={showModal}
      >
        {'custom'}
      </EuiButtonEmpty>,
      getDatePicker(),
    ];
  };

  let errorMessage = '';
  const columns = [
    {
      field: 'deployment',
      name: 'sno',
      width: '10%',
    },
    {
      field: 'creationTime',
      name: 'Update Time',
      render: (time) => {
        return convertEpochToDate(time);
      },
      sortable: true,
    },
    {
      field: 'updatestatus',
      name: 'Update Status',
    },
    {
      field: 'deploymentdetails',
      name: 'Action/ComponentScope/ComponentVersion',
    },
  ];
  const search = {
    toolsRight: renderToolsRight(),
    box: {
      incremental: true,
    },
  };

  const sorting = {
    sort: {
      field: 'creationTime',
      direction: 'desc',
    },
  };

  if (isResponseUpdated && statusCode !== 200) {
    errorMessage =
      'statuscode: ' +
      statusCode +
      '\n' +
      'body ' +
      exceptionRef.current;
  }

  return (
    <ContentPanel title={'Deployment History'}>
      <EuiInMemoryTable
        items={items}
        columns={columns}
        search={search}
        loading={loading || !isResponseUpdated}
        error={errorMessage}
        pagination={pagination}
        sorting={sorting}
      />
    </ContentPanel>
  );
};
